/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, GetSubtenantByIdResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { scheduleInvalidate } from '@/hooks/query/invalidationScheduler';
import { useSubscribeToEvents } from '@/hooks/useSubscribeToEvents';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UseSubtenantByIdQueryProps = {
  subtenantId: string;
};

/**
 * Custom hook that fetches and subscribes to subtenant data updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * Features:
 * - Fetches subtenant data by ID
 * - Auto-invalidates query on relevant WebSocket events
 * - Handles tenant context
 * - Type-safe API response handling
 *
 * @param props.subtenantId - Unique identifier of the subtenant to fetch
 * @returns React Query result containing subtenant data and query state
 *
 * @example
 * ```tsx
 * const { data: subtenant, isLoading } = useSubtenantByIdQuery({ subtenantId: '123' });
 * ```
 */
export function useSubtenantByIdQuery({ subtenantId }: UseSubtenantByIdQueryProps) {
  const queryClient = useQueryClient();
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  useSubscribeToEvents(['subtenant:updated', 'subtenant-membership:updated'], () => {
    scheduleInvalidate(queryClient, ['subtenants', selectedTenantId]);
  });

  return useQuery<GetSubtenantByIdResponse, ApiError>({
    queryKey: ['subtenants', selectedTenantId, subtenantId],
    queryFn: () => {
      return mgmtPlaneAPIClient.subtenants.getSubtenantById({ id: subtenantId, tenantId: selectedTenantId! });
    },
    enabled: !!selectedTenantId && !!subtenantId,
  });
}
